import { DashboardCardSkeleton } from "@/components/dashboard/DashboardCardSkeleton";
import OverViewChart from "@/components/dashboard/OverViewChart";
import PaymentPieChart from "@/components/dashboard/PaymentPieChart";
import TransactionAreaChart from "@/components/dashboard/TransactionAreaChart";
import { Button } from "@/components/ui/Button";
import { Calendar } from "@/components/ui/Calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { QUERY_KEY } from "@/constant/queryKey";
import { prettyFixedAmount } from "@/lib/utils";
import { fetchDashboardData } from "@/services/api-service";
import {
  ADMIN_DASHBOARD_ENDPOINT,
  AdminDashboardStatsData,
} from "@/services/core/admin-dashboard-charts.api";
import { useDashboardStore } from "@/store/dashboard-store";
import { useMerchantStore } from "@/store/merchant-store";
import { useQuery } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { CalendarIcon, DollarSign, LineChart, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { BarChart } from "recharts";
const DashboardPage = () => {
  const { selectedMerchant } = useMerchantStore();
  const { dateRange: storeDataRange, setDateRange: setStoreDateRange } =
    useDashboardStore();
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      setStoreDateRange({
        from: format(range.from, "yyyy-MM-dd"),
        to: format(range.to, "yyyy-MM-dd"),
      });
    }
  };

  useEffect(() => {
    if (dateRange) {
      handleDateRangeChange(dateRange);
    }
  }, [dateRange]);

  const endPoint = `${ADMIN_DASHBOARD_ENDPOINT}/${selectedMerchant?.name}/dashboard-stats`;
  const { data: stats, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.ADMIN_DASHBOARD_STATS,
      selectedMerchant?.name,
      dateRange,
    ],
    queryFn: () =>
      fetchDashboardData<AdminDashboardStatsData>(endPoint, {
        username: selectedMerchant?.name ?? "",
        from: storeDataRange.from,
        to: storeDataRange.to,
      }),
  });

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) {
      return "No previous data";
    }

    const change = ((current - previous) / previous) * 100;
    const sign = change > 0 ? "+" : "";
    return `${sign}${change.toFixed(2)}% from last month`;
  };

  console.log("stats data", stats);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="basis-1/2 space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome to your payment gateway dashboard. Here's an overview of
            your transaction metrics and payment analytics.
          </p>
        </div>

        <div className="flex basis-1/2">
          <div className="ml-auto flex items-center gap-3">
            <h3>Update</h3>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[280px] justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} -{" "}
                        {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 4 }, (_, i) => i).map((i) => (
            <DashboardCardSkeleton key={i} />
          ))
        ) : stats?.payload ? (
          <>
            <DashboardCard
              label="Total Transactions"
              icon={<Wallet className="text-muted-foreground h-4 w-4" />}
              amount={prettyFixedAmount(
                stats.payload.current.totalTransactions ?? 0,
              )}
              desc={calculatePercentageChange(
                stats.payload.current.totalTransactions ?? 0,
                stats.payload.previous.totalTransactions ?? 0,
              )}
            />
            <DashboardCard
              label="Total Amount"
              icon={<DollarSign className="text-muted-foreground h-4 w-4" />}
              amount={`${prettyFixedAmount(stats.payload.current.totalAmount ?? 0)} MMK`}
              desc={calculatePercentageChange(
                stats.payload.current.totalAmount ?? 0,
                stats.payload.previous.totalAmount ?? 0,
              )}
            />
            <DashboardCard
              label="Success Rate"
              icon={<LineChart className="text-muted-foreground h-4 w-4" />}
              amount={`${stats.payload.current.totalSuccess ?? 0}%`}
              desc={calculatePercentageChange(
                stats.payload.current.totalSuccess ?? 0,
                stats.payload.previous.totalSuccess ?? 0,
              )}
            />
            <DashboardCard
              label="Active Channels"
              icon={<BarChart className="text-muted-foreground h-4 w-4" />}
              amount={stats.payload.current.totalActiveChannel ?? 0}
              desc={calculatePercentageChange(
                stats.payload.current.totalActiveChannel ?? 0,
                stats.payload.previous.totalActiveChannel ?? 0,
              )}
            />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => i).map((i) => (
            <DashboardCardSkeleton key={i} />
          ))
        )}
      </div>

      {/* Detailed Analytics */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="channels">Payment Channels</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <OverViewChart />
        </TabsContent>

        <TabsContent value="channels" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <TransactionAreaChart />

            <PaymentPieChart />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardPage;

interface DashboardCardProps {
  label: string;
  icon: React.ReactNode;
  amount: string | number;
  desc: string;
}

const DashboardCard = ({ label, icon, amount, desc }: DashboardCardProps) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{amount}</div>
        <p className="text-muted-foreground text-xs">{desc}</p>
      </CardContent>
    </Card>
  );
};
